import type { SystemRole } from '@/types';
import { apiClient } from './client';

/**
 * Client for `/api/seats/*` (Stream F — Seats, Access & Role Scorecards,
 * ADR-028). Stays in lockstep with `server/src/routes/seats.routes.ts`.
 *
 * A **seat** is an EOS accountability-chart node: a named role with a
 * SystemRole + capability bundle + measurables, arranged into a tree via
 * `parentSeatId`. Seating an Employee (`holderEmployeeId`) grants access;
 * unseating removes ONLY seat-sourced access. One holder per seat (V1);
 * one employee may hold many seats. A `scenario` flag (`current` |
 * `future`) lets an org sketch a growth org alongside the live one.
 *
 * NOTE (backend-parallel): as of this writing the `/api/seats` routes are
 * being built in parallel. This client is typed 1:1 against the ADR-028
 * contract; the chart + seat components import it. Until the routes land,
 * calls will 404 at runtime — that is expected and does not affect
 * typecheck or the mocked component tests.
 */

export type SeatScenario = 'current' | 'future';

/**
 * Roles a seat may carry. `auditor_readonly` is deliberately excluded —
 * seating never grants the auditor seat (it is expiry-managed separately,
 * ADR-028 §D2). Mirrors the server's `seatRoleSchema`.
 */
export const SEAT_ROLES: SystemRole[] = [
  'visionary',
  'integrator',
  'department_lead',
  'quality_lead',
  'qa_personnel',
  'employee',
];

export interface SeatHolder {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string | null;
  /** Null when the holder is a directory-only employee with no portal
   *  account yet — access is stored as intent until they accept an invite. */
  membershipId: string | null;
}

/**
 * The read shape returned by every seat endpoint (`{ data: SeatView }` or
 * `{ data: SeatView[] }`). Mirrors ADR-028's `SeatView`.
 */
export interface SeatView {
  id: string;
  name: string;
  purpose: string | null;
  responsibilities: string[];
  systemRole: SystemRole;
  capabilities: string[];
  departmentId: string | null;
  parentSeatId: string | null;
  holderEmployeeId: string | null;
  hiring: boolean;
  scenario: SeatScenario;
  order: number;
  archivedAt: string | null;
  holderEmployee: SeatHolder | null;
  metricsCount: number;
}

// ── List ────────────────────────────────────────────────────────────────

export interface ListSeatsQuery {
  scenario?: SeatScenario;
  includeArchived?: boolean;
}

export async function listSeats(query: ListSeatsQuery = {}): Promise<SeatView[]> {
  const params = new URLSearchParams();
  if (query.scenario) params.set('scenario', query.scenario);
  if (query.includeArchived) params.set('includeArchived', 'true');
  const qs = params.toString() ? `?${params.toString()}` : '';
  const res = await apiClient.get<{ data: SeatView[] }>(`/api/seats${qs}`);
  return res.data.data;
}

export async function getSeat(id: string): Promise<SeatView> {
  const res = await apiClient.get<{ data: SeatView }>(`/api/seats/${encodeURIComponent(id)}`);
  return res.data.data;
}

// ── Create / update / archive ─────────────────────────────────────────────

export interface CreateSeatRequest {
  name: string;
  purpose?: string | null;
  responsibilities?: string[];
  systemRole: SystemRole;
  capabilities?: string[];
  departmentId?: string | null;
  parentSeatId?: string | null;
  scenario?: SeatScenario;
  order?: number;
}

export async function createSeat(body: CreateSeatRequest): Promise<SeatView> {
  const res = await apiClient.post<{ data: SeatView }>('/api/seats', body);
  return res.data.data;
}

/**
 * Partial update. Pass a field to change it, `null` to clear a nullable
 * one, or omit to leave it. Reparenting is `{ parentSeatId }`; the server
 * runs a cycle guard and re-derives reporting lines.
 */
export interface UpdateSeatRequest {
  name?: string;
  purpose?: string | null;
  responsibilities?: string[];
  systemRole?: SystemRole;
  capabilities?: string[];
  departmentId?: string | null;
  parentSeatId?: string | null;
  order?: number;
}

export async function updateSeat(id: string, body: UpdateSeatRequest): Promise<SeatView> {
  const res = await apiClient.put<{ data: SeatView }>(`/api/seats/${encodeURIComponent(id)}`, body);
  return res.data.data;
}

/** Archives the seat (soft delete). Unassigns the holder + reparents children. */
export async function deleteSeat(id: string): Promise<void> {
  await apiClient.delete(`/api/seats/${encodeURIComponent(id)}`);
}

// ── Assign / unassign / hiring ────────────────────────────────────────────

/**
 * Seat an employee. Throws a 409 `seat_occupied` if the seat already has a
 * holder — the caller confirms a replace and unassigns first. Triggers
 * `recomputeAccessForEmployee` server-side.
 */
export async function assignSeat(id: string, employeeId: string): Promise<SeatView> {
  const res = await apiClient.post<{ data: SeatView }>(`/api/seats/${encodeURIComponent(id)}/assign`, {
    employeeId,
  });
  return res.data.data;
}

export async function unassignSeat(id: string): Promise<SeatView> {
  const res = await apiClient.post<{ data: SeatView }>(`/api/seats/${encodeURIComponent(id)}/unassign`, {});
  return res.data.data;
}

/** Flags/unflags the seat as actively hiring (renders a "Hiring" badge). */
export async function setSeatHiring(id: string, hiring: boolean): Promise<SeatView> {
  const res = await apiClient.post<{ data: SeatView }>(`/api/seats/${encodeURIComponent(id)}/hiring`, {
    hiring,
  });
  return res.data.data;
}

// ── Access preview ────────────────────────────────────────────────────────

/**
 * The access delta seating `employeeId` into this seat would produce.
 * Pure server-side computation — no writes. Feeds AssignSeatModal's
 * confirm step so admins see the role change + granted capabilities
 * before committing.
 */
export interface AccessPreview {
  roleBefore: SystemRole;
  roleAfter: SystemRole;
  capabilitiesAdded: string[];
  capabilitiesAlreadyHeld: string[];
  /** True when the employee has no portal account yet — access is stored
   *  as intent and applied when they accept their invitation. */
  accessPendingInvitation: boolean;
}

export async function getAccessPreview(id: string, employeeId: string): Promise<AccessPreview> {
  const res = await apiClient.get<{ data: AccessPreview }>(
    `/api/seats/${encodeURIComponent(id)}/access-preview?employeeId=${encodeURIComponent(employeeId)}`,
  );
  return res.data.data;
}
